// MaintainDialog — admin-only table maintenance, the flat-menu equivalent of
// Navicat's "Maintain" submenu. Pick an operation (Check / Analyze / Optimize /
// Repair) and, for Check/Repair, a thoroughness level (Quick ↔ Extended) plus
// Repair's "Use FRM"; runs the statement and shows MySQL's result rows.
//
// Props:
//   connectionId  number
//   database      string
//   table         string
//   onClose       fn()
import { createSignal, Show, For } from 'solid-js';
import Modal from './Modal.jsx';
import { maintainTable } from '../lib/maintain.js';
import { toast } from '../lib/toast.js';

const OPS = [
  { value: 'check',    label: 'Check' },
  { value: 'analyze',  label: 'Analyze' },
  { value: 'optimize', label: 'Optimize' },
  { value: 'repair',   label: 'Repair' },
];

export default function MaintainDialog(props) {
  const [op, setOp] = createSignal('check');
  const [level, setLevel] = createSignal('default'); // 'default' | 'quick' | 'extended'
  const [useFrm, setUseFrm] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [result, setResult] = createSignal(null); // { columns, rows }

  // Quick/Extended apply to CHECK + REPAIR; USE FRM is REPAIR-only.
  const hasLevel = () => op() === 'check' || op() === 'repair';
  const changeOp = (v) => { setOp(v); setResult(null); };

  const run = async () => {
    setBusy(true);
    setResult(null);
    const options = [];
    if (hasLevel() && level() !== 'default') options.push(level());
    if (op() === 'repair' && useFrm()) options.push('use_frm');
    try {
      const r = await maintainTable(props.connectionId, props.database, props.table, op(), options);
      setResult({ columns: r.columns || [], rows: r.rows || [] });
      const bad = (r.rows || []).some((row) => row.some((c) => /error/i.test(String(c))));
      if (!bad) toast.success(`${op()} done on “${props.table}”`);
    } catch (e) {
      toast.error('Maintenance failed: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  // Index of the Msg_type column (if present) → row tint by status.
  const typeIdx = () => (result()?.columns || []).findIndex((c) => /msg_type/i.test(c));
  const rowClass = (row) => {
    const i = typeIdx();
    const t = i >= 0 ? String(row[i] || '').toLowerCase() : '';
    return t === 'error' ? 'maintain-err' : (t === 'warning' || t === 'note') ? 'maintain-warn' : '';
  };

  return (
    <Modal open={true} title={`Maintain table — ${props.table}`} onClose={props.onClose} wide>
      <div class="maintain-dialog">
        <div class="maintain-controls">
          <label class="field-label">Operation</label>
          <select class="input" style="width:auto;" value={op()} disabled={busy()}
                  onChange={(e) => changeOp(e.currentTarget.value)}>
            <For each={OPS}>{(o) => <option value={o.value}>{o.label}</option>}</For>
          </select>

          <Show when={hasLevel()}>
            <span class="maintain-levels">
              <For each={[['default', 'Default'], ['quick', 'Quick'], ['extended', 'Extended']]}>
                {([v, lbl]) => (
                  <label class="checkbox-label">
                    <input type="radio" name="maintain-level" checked={level() === v}
                           disabled={busy()} onChange={() => setLevel(v)} /> {lbl}
                  </label>
                )}
              </For>
            </span>
          </Show>
          <Show when={op() === 'repair'}>
            <label class="checkbox-label" title="Rebuild the index from the .frm definition (when the index header itself is corrupt)">
              <input type="checkbox" checked={useFrm()} disabled={busy()}
                     onChange={(e) => setUseFrm(e.currentTarget.checked)} /> Use FRM
            </label>
          </Show>

          <div class="spacer" />
          <button class="btn" onClick={run} disabled={busy()}>{busy() ? 'Running…' : '▶ Run'}</button>
        </div>

        <p class="muted" style="font-size:12px;margin:6px 0 0;">
          Runs <span class="mono">{op().toUpperCase()} TABLE `{props.table}`</span>. Check/Repair apply to
          MyISAM-type engines — on InnoDB the server returns a note. Large tables can take a while and may
          lock the table.
        </p>

        <Show when={result()}>
          <Show when={result().rows.length > 0}
                fallback={<div class="muted" style="padding:10px 0;">No result rows returned.</div>}>
            <table class="maintain-result">
              <thead><tr><For each={result().columns}>{(c) => <th>{c}</th>}</For></tr></thead>
              <tbody>
                <For each={result().rows}>
                  {(row) => <tr class={rowClass(row)}><For each={row}>{(cell) => <td>{cell}</td>}</For></tr>}
                </For>
              </tbody>
            </table>
          </Show>
        </Show>

        <div style="display:flex;justify-content:flex-end;margin-top:14px;">
          <button class="btn btn-ghost" onClick={props.onClose} disabled={busy()}>Close</button>
        </div>
      </div>
    </Modal>
  );
}
